import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Spinner } from "@fluentui/react-components";
import { Search } from "lucide-react";

import {
  AdminJobChecklist as AdminJobChecklistType,
  useAdminJobChecklistQuery,
} from "@/queries/admin/job-checklist";

import CompanyTable from "./company-table";

const AdminJobChecklist = () => {
  const [searchString, setSearchString] = useState("");

  const { adminJobChecklist, adminJobChecklistLoading } =
    useAdminJobChecklistQuery();

  const mergedJobs = useMemo(() => {
    // Filter and group jobs by company
    const companyIdToAdminJobChecklists: Record<
      string,
      AdminJobChecklistType[]
    > = {};

    if (searchString) {
      const filterString = searchString.toLocaleLowerCase();

      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      adminJobChecklist
        ?.filter(
          (job) =>
            job.role?.toLocaleLowerCase().includes(filterString) ||
            job.company.name?.toLocaleLowerCase().includes(filterString),
        )
        .forEach((job) =>
          (companyIdToAdminJobChecklists[job.company.id] ??= []).push(job),
        );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    } else {
      adminJobChecklist?.forEach((job) =>
        (companyIdToAdminJobChecklists[job.company.id] ??= []).push(job),
      );
    }

    // Convert the grouped jobs into an array of companies with their jobs
    return Object.keys(companyIdToAdminJobChecklists)
      .map((companyId) => {
        const jobs =
          companyIdToAdminJobChecklists[companyId]?.sort((a, b) =>
            (a.role ?? "").localeCompare(b.role ?? ""),
          ) ?? [];
        const companyName = jobs[0]?.company.name;
        const companyLogoLink = jobs[0]?.company.logoLink;

        return {
          id: companyId,
          name: companyName,
          logoLink: companyLogoLink,
          jobs,
        };
      })
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [adminJobChecklist, searchString]);

  if (adminJobChecklistLoading) {
    return (
      <div>
        <p className="py-4 text-lg text-[#181d27] font-semibold">
          Company Checklists
        </p>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="py-4 w-full flex flex-col gap-4 items-start self-stretch">
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <p className="text-lg text-[#181d27] font-semibold">
            Company Checklists
          </p>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <CompanyTable companies={mergedJobs} />
      </div>
    </div>
  );
};

export default AdminJobChecklist;
